/* RoyAI — AI Royalty & Finance Assistant for BeatCredsi.
 *
 * Section layout (matches Dashboard.jsx cadence — color-coded SectionBands):
 *   01 Royalty Dashboard   (KPIs + charts)
 *   02 Royalty Hunter      (missing opps)
 *   03 Metadata Audit
 *   04 AI Accounting
 *   05 Forecast Engine
 *   06 AI Alerts
 */
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import KpiCard from "@/components/royai/KpiCard";
import TrackAuditForm from "@/components/royai/TrackAuditForm";
import SmartAuditLink from "@/components/royai/SmartAuditLink";
import DeepDiveRevenueReport from "@/components/royai/DeepDiveRevenueReport";
import EvidenceVault from "@/components/EvidenceVault";
import RoyaltyAnalyzer from "@/components/RoyaltyAnalyzer";
import CatalogSummary from "@/components/royai/CatalogSummary";
import RoyaiLockGate from "@/components/RoyaiLockGate";
import TeaserOverlay from "@/components/TeaserOverlay";
import { api, formatApiError } from "@/lib/api";
import { royai, fmtUsd, fmtUsdCents } from "@/lib/royai";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  DollarSign, Clock, AlertTriangle, TrendingUp, Search, Shield, Calculator,
  LineChart as LineIcon, Bell, Download, Sparkles, Plus, Minus, ArrowRight,
  Lock, X,
} from "lucide-react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { useLiveEvents } from "@/lib/liveEvents";

const CHART_COLORS = ["#D4AF37", "#F5C842", "#E1B84B", "#B8860B", "#10B981", "#38BDF8", "#A78BFA"];

function Band({ number, title, accent = "#D4AF37", open = true, onToggle = null, meta = null }) {
  const clickable = typeof onToggle === "function";
  // Test id uses `number` when provided (legacy sections 1–6) or a slugified
  // title for the newer named collapsibles (SAL, UGC, etc.). The visual
  // header no longer shows the numeric prefix — a cleaner luxury look
  // per the design refresh.
  const testId = number != null
    ? `royai-band-${number}`
    : `royai-band-${String(title).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;
  const inner = (
    <>
      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: accent, boxShadow: `0 0 14px ${accent}99` }} />
      <span className="font-mono-tech text-sm sm:text-base uppercase tracking-[0.2em] font-semibold shrink-0" style={{ color: accent }}>
        {title}
      </span>
      <span className="flex-1 h-px" style={{ background: `linear-gradient(to right, ${accent}55, transparent)` }} />
      {meta && <span className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500 shrink-0">{meta}</span>}
      {clickable && (
        <span
          className="text-neutral-500 hover:text-white text-lg font-mono-tech leading-none transition-transform shrink-0"
          style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
          aria-hidden
        >
          ›
        </span>
      )}
    </>
  );
  if (clickable) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="w-full my-8 first:mt-0 flex items-center gap-4 group text-left"
        aria-expanded={open}
        data-testid={testId}
      >
        {inner}
      </button>
    );
  }
  return (
    <div data-testid={testId} className="my-8 first:mt-0 flex items-center gap-4">
      {inner}
    </div>
  );
}

function SectionCard({ children, className = "" }) {
  return <div className={`border border-white/5 bg-[#0c0c0c] p-6 lg:p-8 ${className}`}>{children}</div>;
}

function ManualEntryToggle({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs font-mono-tech uppercase tracking-[0.22em] text-neutral-400 hover:text-amber-400 border border-white/10 bg-black/40 px-3 py-2 flex items-center gap-2"
        data-testid="toggle-manual-entry"
      >
        <span className={`transition-transform ${open ? "rotate-90" : ""}`}>›</span>
        Advanced / Manual Entry
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

const SEVERITY_STYLES = {
  high:   "border-rose-500/50   bg-rose-500/5   text-rose-300",
  medium: "border-amber-500/50  bg-amber-500/5  text-amber-300",
  low:    "border-cyan-500/40   bg-cyan-500/5   text-cyan-300",
};

export default function RoyAI() {
  const [access, setAccess] = useState(null);       // {unlocked, is_master, requires_code}
  const [accessLoading, setAccessLoading] = useState(true);
  const [d, setD] = useState(null);         // dashboard
  const [h, setH] = useState(null);         // hunter
  const [au, setAu] = useState(null);       // audit
  const [ac, setAc] = useState(null);       // accounting
  const [fc, setFc] = useState(null);       // forecast
  const [al, setAl] = useState(null);       // alerts
  const [ins, setIns] = useState(null);     // insights
  const [bn, setBn] = useState(null);       // proactive bounties
  const [rr, setRr] = useState(null);       // deep-dive revenue report
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ category: "studio", description: "", amount_usd: "" });
  // Collapsible section open/closed state. Persisted to localStorage so
  // creators pick up right where they left off. Keys `1`–`6` are the legacy
  // numbered sections; `sal` (Smart Audit Link) and `ugc` (Royalty & UGC
  // Opportunity Analyzer) are newer named collapsibles on the Audit tab.
  const SECTIONS_STORAGE_KEY = "royai:openSections:v2";
  const DEFAULT_OPEN = { 1: true, 2: true, 3: true, 4: true, 5: true, 6: true, sal: true, ugc: false };
  const [openSections, setOpenSections] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_OPEN;
    try {
      const saved = JSON.parse(window.localStorage.getItem(SECTIONS_STORAGE_KEY) || "null");
      return saved && typeof saved === "object" ? { ...DEFAULT_OPEN, ...saved } : DEFAULT_OPEN;
    } catch {
      return DEFAULT_OPEN;
    }
  });
  const toggleSection = (n) => setOpenSections((s) => {
    const next = { ...s, [n]: !s[n] };
    try { window.localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  // 10-icon launcher — each tile represents one specific RoyAI feature.
  // Clicking a tile flips the parent tab into view, force-opens that
  // feature's collapsible band, and collapses siblings so the user
  // focuses on a single feature at a time. `key` is the short label
  // (SAL, DDRR, …) displayed on the tile; `label` is the full feature
  // name shown as sub-caption and hover title.
  const [activeTab, setActiveTab] = useState("audit");
  const [activeFeature, setActiveFeature] = useState(null);
  const FEATURES = [
    { key: "SAL",  title: "Smart Audit Link",                     category: "Metadata Authority",     desc: "Universal link metadata importer to map your tracks instantly.",                    tab: "audit",    section: "sal",  accent: "#10B981", Icon: Sparkles,     locked: true },
    { key: "DDRR", title: "Deep Dive Revenue Report",             category: "Royalty Infrastructure", desc: "A forensic multi-platform breakdown of streams and revenue.",                        tab: "audit",    section: 3,      accent: "#D4AF37", Icon: Search,       locked: true },
    { key: "RUOA", title: "Royalty & UGC Opportunity Analyzer",   category: "Viral Discovery",        desc: "Map your TikTok and Shorts social views to real dollars.",                            tab: "audit",    section: "ugc",  accent: "#A78BFA", Icon: TrendingUp,   locked: true },
    { key: "RH",   title: "Royalty Hunter",                       category: "Revenue Recovery",       desc: "A live bounty board for finding and claiming missing registrations.",                 tab: "recovery", section: 2,      accent: "#E11D48", Icon: Bell,         locked: true },
    { key: "LH",   title: "Legacy Hunter",                        category: "Catalog Valuation",      desc: "A specialized scan for old ISRCs and back-pay from forgotten hits.",                  tab: "recovery", section: "lh",   accent: "#F59E0B", Icon: Clock,        comingSoon: true },
    { key: "RD",   title: "Royalty Dashboard",                    category: "Business Intelligence",  desc: "Your real-time command center for all earning and request stats.",                   tab: "finance",  section: 1,      accent: "#38BDF8", Icon: LineIcon,     locked: true },
    { key: "FE",   title: "Royalty Forecast Engine",              category: "Financial Planning",     desc: "12-month predictive modeling based on your career trends.",                           tab: "finance",  section: 5,      accent: "#A78BFA", Icon: TrendingUp,   locked: true },
    { key: "AIA",  title: "AI Accounting",                        category: "Business Structure",     desc: "A professional P&L command center for studio and equipment expenses.",                tab: "finance",  section: 4,      accent: "#D4AF37", Icon: Calculator,   locked: true },
    { key: "VE",   title: "Verified Evidence",                    category: "Rights Protection",      desc: "The forensic vault for storing and verifying your legal receipts.",                   tab: "evidence", section: null,   accent: "#E11D48", Icon: Shield,       locked: true },
    { key: "CS",   title: "Catalog Summary",                      category: "Performance Portfolio",  desc: "The massive totalizer that sums up your entire career value.",                        tab: "catalog",  section: null,   accent: "#D4AF37", Icon: DollarSign,   locked: true },
  ];

  // Per-tool unlock state derives entirely from `/royai/access-status`:
  // • Master/Owner/Founding-Producer → permanent, no timer, everything unlocked.
  // • Redeemed VIP code → temporary unlock scoped to the code's expires_at
  //   (60-minute TTL). When the timer runs out we re-fetch and flip the
  //   whole grid back to the Locked state.
  // Feature currently being unlocked — drives the modal.
  const [unlockingFeature, setUnlockingFeature] = useState(null);
  const [unlockCode, setUnlockCode] = useState("");
  const [unlockBusy, setUnlockBusy] = useState(false);
  const submitUnlock = async (e) => {
    e?.preventDefault();
    const trimmed = unlockCode.trim().toUpperCase();
    if (!trimmed) { toast.error("Enter your daily VIP code."); return; }
    setUnlockBusy(true);
    try {
      await api.post("/royai/redeem-code", { code: trimmed });
      toast.success("Access granted. RoyAI Suite unlocked.");
      const feat = unlockingFeature;
      setUnlockingFeature(null);
      setUnlockCode("");
      // Refresh access-status so the countdown + unlock state comes
      // straight from the server (single source of truth).
      await checkAccess();
      if (feat) setTimeout(() => focusFeature(feat), 150);
    } catch (err) {
      toast.error(formatApiError(err) || "That code could not be verified.");
    } finally {
      setUnlockBusy(false);
    }
  };

  // Focus a single feature: switch to its parent tab, open its band,
  // collapse sibling bands within the same tab so the user sees only
  // the feature they picked. Persists to localStorage via toggleSection.
  // Focus a single feature — tile-driven visibility. Clicking a tile
  // that is already active toggles it OFF (all sections hidden again).
  // Otherwise it activates that feature exclusively and scrolls the
  // matching pane into view.
  const focusFeature = (feat) => {
    const isSame = activeFeature === feat.key;
    setActiveFeature(isSame ? null : feat.key);
    if (isSame) return;
    setActiveTab(feat.tab);
    setTimeout(() => {
      try {
        const el = document.querySelector(`[data-testid="royai-feature-pane-${feat.key}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch { /* ignore */ }
    }, 120);
  };

  async function checkAccess() {
    try {
      const { data } = await api.get("/royai/access-status");
      setAccess(data);
      return data;
    } catch {
      setAccess({ unlocked: false, is_master: false, requires_code: true });
      return null;
    } finally {
      setAccessLoading(false);
    }
  }

  async function loadAll() {
    try {
      const [dv, hv, av, acv, fv, alv, iv, bv, rrv] = await Promise.all([
        royai.dashboard(), royai.hunter(), royai.audit(),
        royai.accounting(), royai.forecast(), royai.alerts(), royai.insights(),
        royai.bounties(),
        royai.revenueReport(),
      ]);
      setD(dv); setH(hv); setAu(av); setAc(acv); setFc(fv); setAl(alv); setIns(iv); setBn(bv); setRr(rrv);
    } catch (e) {
      toast.error("Failed to load RoyAI");
    }
    setLoading(false);
  }
  useEffect(() => {
    (async () => {
      const a = await checkAccess();
      if (a?.unlocked) loadAll();
      else setLoading(false);
    })();
  }, []);

  // Session countdown — ticks every second while a session-based unlock
  // is active. When the timer hits 0 we re-fetch access-status so the
  // backend can revoke and the whole grid flips back to Locked.
  const [sessionRemaining, setSessionRemaining] = useState(null);
  useEffect(() => {
    if (!access?.unlocked || access?.permanent || !access?.session_expires_at) {
      setSessionRemaining(null);
      return undefined;
    }
    const target = new Date(access.session_expires_at).getTime();
    const tick = () => {
      const secs = Math.max(0, Math.floor((target - Date.now()) / 1000));
      setSessionRemaining(secs);
      if (secs === 0) {
        // Session expired — re-check access to flip the UI back to
        // the locked/unlocked truth from the server.
        checkAccess();
        clearInterval(id);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [access?.unlocked, access?.permanent, access?.session_expires_at]);

  // Scroll to Evidence Vault when the URL hash requests it (used by the
  // onboarding "Upload evidence" CTA).
  useEffect(() => {
    if (typeof window === "undefined" || window.location.hash !== "#evidence") return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="evidence-vault-royai"]')
        || document.querySelector('[data-testid="evidence-vault"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 800);
    return () => clearTimeout(t);
  }, [loading]);

  // Real-time RoyAI updates: when admin approves evidence for this user or
  // the proactive audit modifies bounties on the backend, refetch so
  // Recoverable / Bounties / Audit score stay live without a refresh.
  useLiveEvents(["evidence.approved", "audit.updated", "bounty.resolved"], (evt) => {
    loadAll();
    const detail = evt?.data;
    if (evt?.type === "evidence.approved" && detail) {
      const resolved = (detail.resolved_bounties_direct || 0) + (detail.resolved_bounties_by_audit || 0);
      if (resolved > 0) {
        toast.success(`Evidence approved — ${resolved} bounty(s) resolved · Score +${detail.score_delta || 0}`);
      } else {
        toast.success(`Evidence approved · Score +${detail.score_delta || 0}`);
      }
    }
  });

  async function runProactiveAudit() {
    setScanning(true);
    try {
      const r = await royai.runAudit();
      if (r.ok) {
        toast.success(`Audit complete — ${r.bounties_created} new bounty(s) found across ${r.tracks_found} tracks.`);
        setBn(await royai.bounties());
      } else {
        toast.warning(r.reason === "assessment_incomplete"
          ? "Complete your assessment first to unlock the audit."
          : r.reason === "no_profile_links"
            ? "Link at least one music profile (Spotify, Muso.ai, Genius) to run the audit."
            : "Audit could not run.");
      }
    } catch { toast.error("Audit failed"); }
    setScanning(false);
  }

  async function dismissBounty(id) {
    try {
      await royai.dismissBounty(id);
      setBn(await royai.bounties());
    } catch { toast.error("Failed to dismiss"); }
  }

  async function submitExpense(e) {
    e.preventDefault();
    if (!form.description || !form.amount_usd) return;
    try {
      await royai.addExpense({ ...form, amount_usd: parseFloat(form.amount_usd) });
      toast.success("Expense added");
      setForm({ category: "studio", description: "", amount_usd: "" });
      const acv = await royai.accounting(); setAc(acv);
    } catch { toast.error("Failed to add expense"); }
  }

  if (accessLoading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-24 text-center text-neutral-500 font-mono-tech">
          Checking access…
        </div>
      </div>
    );
  }

  if (access && !access.unlocked) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <RoyaiLockGate
          activeClaim={access?.active_claim}
          onUnlock={async () => {
            await checkAccess();
            setLoading(true);
            await loadAll();
          }}
        />
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-24 text-center text-neutral-500 font-mono-tech" data-testid="royai-loading">
          Loading RoyAI…
        </div>
      </div>
    );
  }

  const totals = d?.totals || {};
  const monthlyData = (d?.monthly_earnings || []).map((m) => ({ name: m.month, earnings: m.earnings }));
  const sourceData = (d?.revenue_by_source || []).map((s) => ({ name: s.source, value: s.amount_usd }));
  const trendData = (fc?.trend || []).map((t) => ({ name: t.month, predicted: t.predicted }));

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-12">
        {/* Page Header */}
        <div className="mb-10">
          <div className="text-overline mb-3">
            RoyAI<span className="text-amber-500">™</span> · AI Royalty & Finance Assistant
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-4">
            Every dollar. <span className="text-amber-500">Every source.</span> Every gap.
          </h1>
          <p className="text-neutral-400 max-w-3xl leading-relaxed">
            Your AI-driven royalty command center. Discover missing income, audit metadata, forecast earnings, and manage your creator finances in one place. Data below is <span className="text-amber-400">placeholder</span> until you connect your distributor and publishing accounts.
          </p>
          <a
            href={royai.reportUrl()}
            data-testid="royai-download-report"
            className="inline-flex items-center gap-2 mt-6 px-4 py-2 border border-amber-500/40 hover:bg-amber-500/10 hover:border-amber-500 transition-colors text-sm font-mono-tech uppercase tracking-widest"
          >
            <Download size={14} /> Download P&L Report
          </a>
        </div>

        {/* Session Countdown — shown to users on a temporary 60-min VIP
            unlock. Founding-Producer + master accounts have `permanent`
            access and never see this. When the timer hits 0, the client
            re-fetches /royai/access-status and the whole grid auto-flips
            back to the Locked state. */}
        {access?.unlocked && !access?.permanent && sessionRemaining !== null && (
          <div
            data-testid="royai-session-countdown"
            className="mb-6 flex items-center justify-between gap-4 border border-amber-500/40 bg-amber-500/5 px-5 py-4"
          >
            <div className="flex items-center gap-3">
              <Clock size={18} className="text-amber-500 shrink-0" />
              <div>
                <div className="text-[10px] font-mono-tech uppercase tracking-[0.22em] text-amber-500">
                  VIP Session Active
                </div>
                <div className="text-xs text-neutral-300 mt-0.5">
                  Your daily-drop code unlocks the entire suite for the next 60 minutes.
                </div>
              </div>
            </div>
            <div
              className="font-mono-tech text-2xl sm:text-3xl font-semibold text-amber-500 tabular-nums"
              data-testid="royai-session-timer"
            >
              {String(Math.floor(sessionRemaining / 60)).padStart(2, "0")}
              :{String(sessionRemaining % 60).padStart(2, "0")}
            </div>
          </div>
        )}

        {/* Icon-Grid Launcher — 10-feature app-launcher style navigation.
            Each tile is one specific RoyAI feature (SAL, DDRR, RUOA, RH,
            LH, RD, FE, AIA, VE, CS). The short code is the primary label;
            the full feature name is the sub-caption AND hover title.
            Clicking a tile focuses that feature — switches to the parent
            tab and opens only that collapsible band. Grid is responsive:
            2 cols mobile → 5 cols desktop. */}
        <div
          role="tablist"
          aria-label="RoyAI feature launcher"
          data-testid="royai-launcher"
          className="mb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {FEATURES.map((f) => {
            const active = activeFeature === f.key;
            const Icon = f.Icon;
            const allowed = access?.allowed_features || [];
            const comingSoonList = access?.coming_soon_features || [];
            const isComingSoon = f.comingSoon || comingSoonList.includes(f.key);
            const isUnlocked = !isComingSoon && (!f.locked || allowed.includes(f.key));
            const ctaLabel = isComingSoon ? "Coming Soon" : (isUnlocked ? "Launch App" : "Unlock Tool");
            const CtaIcon = isComingSoon ? Clock : (isUnlocked ? ArrowRight : Lock);
            const handleClick = () => {
              if (isComingSoon) { toast.info(`${f.title} — coming August 2026.`); return; }
              if (!isUnlocked) { setUnlockingFeature(f); return; }
              focusFeature(f);
            };
            return (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={active}
                title={f.title}
                data-testid={`royai-feature-${f.key}`}
                onClick={handleClick}
                className={`group relative text-left transition-all duration-200 border overflow-hidden ${
                  active
                    ? "bg-[#0f0f0f] border-transparent -translate-y-0.5"
                    : "bg-[#0a0a0a] border-white/5 hover:bg-[#0f0f0f] hover:-translate-y-0.5"
                }`}
                style={
                  active
                    ? {
                        borderColor: `${f.accent}66`,
                        boxShadow: `inset 0 0 0 1px ${f.accent}33, 0 20px 40px -20px ${f.accent}aa`,
                      }
                    : {}
                }
              >
                {/* Top accent bar — always visible, brightens when active */}
                <span
                  className="absolute top-0 inset-x-0 h-[3px] transition-opacity"
                  style={{ background: f.accent, opacity: active ? 1 : 0.7 }}
                />

                {/* Coming Soon ribbon (Legacy Hunter) or Lock icon (locked features) */}
                {isComingSoon ? (
                  <span
                    data-testid={`royai-feature-${f.key}-coming-soon`}
                    className="absolute top-3 right-3 z-10 px-2 py-0.5 border border-amber-500/50 bg-amber-500/10 text-[9px] font-mono-tech uppercase tracking-[0.2em] text-amber-400 font-semibold"
                  >
                    Coming Aug 2026
                  </span>
                ) : !isUnlocked ? (
                  <span
                    data-testid={`royai-feature-${f.key}-lock-icon`}
                    className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center border border-white/10 bg-black/60"
                  >
                    <Lock size={12} className="text-neutral-400" strokeWidth={2} />
                  </span>
                ) : null}                <div className="p-6">
                  {/* Header row — Category label + icon */}
                  <div className="flex items-start justify-between gap-4 mb-6">
                    <div
                      className="text-[10px] font-mono-tech uppercase tracking-[0.22em] leading-relaxed"
                      style={{ color: f.accent }}
                    >
                      {f.category}
                    </div>
                    <div
                      className="w-9 h-9 rounded-full border flex items-center justify-center shrink-0 transition-transform group-hover:scale-105"
                      style={{
                        borderColor: `${f.accent}55`,
                        background: active ? `${f.accent}18` : `${f.accent}08`,
                        boxShadow: active ? `0 0 16px ${f.accent}66` : "none",
                      }}
                    >
                      <Icon size={16} strokeWidth={1.6} style={{ color: f.accent }} />
                    </div>
                  </div>

                  {/* Feature title — bright, large, wraps */}
                  <h3 className="font-display text-2xl leading-tight text-white mb-3">
                    {f.title}
                  </h3>

                  {/* Abbreviation pill */}
                  <div
                    className="inline-flex items-center px-2 py-0.5 border mb-4"
                    style={{
                      borderColor: `${f.accent}55`,
                      background: `${f.accent}10`,
                    }}
                  >
                    <span
                      className="text-[10px] font-mono-tech font-semibold tracking-[0.22em]"
                      style={{ color: f.accent }}
                    >
                      {f.key}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-xs text-neutral-400 leading-relaxed mb-5">
                    {f.desc}
                  </p>

                  {/* Action button — gold pill, matches Academy CTA style.
                      Locked features (like Legacy Hunter) show "UNLOCK TOOL",
                      everything else says "LAUNCH APP". The whole tile is
                      already clickable — this reinforces the CTA visually. */}
                  <div
                    className={`inline-flex items-center gap-2 px-4 py-2 border transition-all font-mono-tech text-[11px] uppercase tracking-[0.22em] font-semibold ${
                      f.locked
                        ? "border-amber-500/40 bg-amber-500/5 text-amber-400 group-hover:bg-amber-500/15 group-hover:border-amber-500/70"
                        : "border-amber-500 bg-amber-500 text-black group-hover:brightness-110"
                    }`}
                    style={
                      !f.locked
                        ? { boxShadow: "0 8px 24px -12px #D4AF37" }
                        : {}
                    }
                  >
                    <span>{f.locked ? "Unlock Tool" : "Launch App"}</span>
                    <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Tool sections — all revealed via tile clicks. */}
        {/* 01 — ROYALTY DASHBOARD (Finance tab) */}
        {activeFeature === "RD" && (<div data-testid="royai-feature-pane-RD">
        <Band number={1} title="Royalty Dashboard" open={openSections[1]} onToggle={() => toggleSection(1)} />
        {openSections[1] && (<div data-testid="royai-content-1">
        <TeaserOverlay featureKey="royalty_dashboard">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5 mb-6">
          <KpiCard testId="kpi-total-earned"    label="Total Royalties Earned" value={fmtUsd(totals.total_earned)}    icon={DollarSign}     delta={12.4} />
          <KpiCard testId="kpi-pending"         label="Pending Royalties"       value={fmtUsd(totals.pending)}         icon={Clock}          delta={-2.1} accent="#38BDF8" />
          <KpiCard testId="kpi-missing"         label="Missing Royalties"       value={fmtUsd(totals.missing)}         icon={AlertTriangle}  delta={-8.7} accent="#E11D48" />
          <KpiCard testId="kpi-unpaid"          label="Est. Unpaid Royalties"   value={fmtUsd(totals.estimated_unpaid)} icon={TrendingUp}    delta={4.2} accent="#10B981" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/5 border border-white/5 mb-6">
          <SectionCard className="lg:col-span-2 !border-0">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl font-medium">Monthly Earnings</h3>
              <span className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">Last 12 months</span>
            </div>
            <div className="h-64" data-testid="chart-monthly">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyData}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.3)" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="rgba(255,255,255,0.3)" fontSize={11} tickFormatter={(v) => `$${(v/1000).toFixed(1)}k`} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(212,175,55,0.4)", fontSize: 12 }}
                    formatter={(v) => fmtUsdCents(v)}
                  />
                  <Line type="monotone" dataKey="earnings" stroke="#D4AF37" strokeWidth={2}
                    dot={{ fill: "#D4AF37", r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>

          <SectionCard className="!border-0">
            <h3 className="font-display text-xl font-medium mb-6">Revenue by Source</h3>
            <div className="h-64" data-testid="chart-sources">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={sourceData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {sourceData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(212,175,55,0.4)", fontSize: 12 }} formatter={(v) => fmtUsdCents(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-1.5 max-h-32 overflow-y-auto">
              {sourceData.slice(0, 4).map((s, i) => (
                <div key={s.name} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2 truncate">
                    <span className="w-2 h-2 shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="truncate text-neutral-300">{s.name}</span>
                  </span>
                  <span className="text-neutral-400 font-mono-tech shrink-0 ml-2">{fmtUsd(s.value)}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* AI INSIGHTS PANEL */}
        {ins && (
          <SectionCard className="mb-6 border-amber-500/25 bg-gradient-to-br from-amber-500/[0.04] to-transparent">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full border border-amber-500/50 bg-black/60 flex items-center justify-center shrink-0">
                <Sparkles size={20} className="text-amber-400" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-mono-tech uppercase tracking-[0.22em] text-amber-500 mb-2">RoyAI Insight</div>
                <p data-testid="royai-insight" className="text-sm sm:text-base text-neutral-200 leading-relaxed">{ins.insight}</p>
              </div>
            </div>
          </SectionCard>
        )}
        </TeaserOverlay>

        {/* 02 — ROYALTY HUNTER */}
        </div>)}
        </div>)}
        {activeFeature === "RH" && (<div data-testid="royai-feature-pane-RH">
        <Band number={2} title="Royalty Hunter" accent="#E11D48" open={openSections[2]} onToggle={() => toggleSection(2)} />
        {openSections[2] && (<div data-testid="royai-content-2">

        {/* Proactive Profile Audit — Actionable Next Moves generated from
            MusicBrainz cross-check of linked profiles vs Evidence Vault. */}
        <SectionCard className="mb-6 border-amber-500/30">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="text-overline mb-1">Actionable Next Moves · Proactive Profile Audit</div>
              <div className="text-xs text-neutral-500">
                {bn?.last_run_at
                  ? <>Last scan: <span className="text-neutral-300">{new Date(bn.last_run_at).toLocaleString()}</span> · <span className="text-neutral-300">{bn.tracks_found}</span> tracks found via MusicBrainz</>
                  : "Never scanned. Complete your assessment and link a profile (Spotify / Muso.ai / Genius) to unlock."}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {bn?.total_recoverable_usd > 0 && (
                <div className="text-right">
                  <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">Recoverable</div>
                  <div className="font-display text-2xl text-amber-400" data-testid="bounties-total">{fmtUsd(bn.total_recoverable_usd)}</div>
                </div>
              )}
              <button
                onClick={runProactiveAudit}
                disabled={scanning}
                data-testid="run-audit-btn"
                className="inline-flex items-center gap-2 px-4 py-2 border border-amber-500/50 hover:bg-amber-500/10 hover:border-amber-500 text-sm font-mono-tech uppercase tracking-widest text-amber-400 disabled:opacity-50"
              >
                <RefreshCw size={14} className={scanning ? "animate-spin" : ""} />
                {scanning ? "Scanning…" : "Re-run Audit"}
              </button>
            </div>
          </div>

          {(bn?.bounties || []).length === 0 ? (
            <div className="text-sm text-neutral-500 border border-dashed border-white/10 px-4 py-6 text-center">
              {bn?.last_run_at
                ? "No bounties — every tracked release has verified evidence in your vault."
                : "Run your first audit to auto-discover unverified tracks."}
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="bounties-list">
              {(bn.bounties || []).map((b) => (
                <div key={b.id} className="border border-amber-500/20 bg-black/40 px-4 py-3 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono-tech uppercase tracking-widest px-2 py-0.5 border border-amber-500/50 bg-amber-500/10 text-amber-300">BOUNTY</span>
                      {b.isrcs?.[0] && (
                        <span className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">ISRC: {b.isrcs[0]}</span>
                      )}
                      {b.source_artist && (
                        <span className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">via {b.source_artist}</span>
                      )}
                    </div>
                    <div className="font-medium text-white mb-1 truncate">{b.title}</div>
                    <div className="text-xs text-neutral-400 leading-relaxed">{b.recommended_action}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className="text-right">
                      <div className="text-xs font-mono-tech text-neutral-500">Est. Recovery</div>
                      <div className="font-display text-lg text-emerald-400">{fmtUsd(b.estimated_recovery_usd)}</div>
                    </div>
                    <button
                      onClick={() => dismissBounty(b.id)}
                      title="Dismiss"
                      data-testid={`dismiss-${b.id}`}
                      className="text-neutral-500 hover:text-rose-400 text-xs font-mono-tech uppercase tracking-widest"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard className="mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-3">
              <Search size={20} className="text-amber-400" />
              <h3 className="font-display text-xl font-medium">Track-Specific Bounties</h3>
            </div>
            <div className="text-right">
              <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">Total Recoverable</div>
              <div className="font-display text-2xl text-amber-400" data-testid="hunter-total">{fmtUsd(h?.total_recoverable_usd)}</div>
            </div>
          </div>
          {(h?.opportunities || []).length === 0 ? (
            <div className="text-sm text-neutral-500 border border-dashed border-white/10 px-4 py-6 text-center">
              No bounties yet. Paste a MusicBrainz / Muso.AI / AllMusic / Genius URL in Section 03 to generate track-specific bounties.
            </div>
          ) : (
            <div className="space-y-3" data-testid="hunter-list">
              {(h?.opportunities || []).map((o, i) => (
                <div key={i} className="border border-amber-500/20 bg-black/40 px-4 py-4 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs font-mono-tech uppercase tracking-widest px-2 py-0.5 border ${SEVERITY_STYLES[o.severity]}`}>
                        {o.severity}
                      </span>
                      <span className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">{o.platform || o.area}</span>
                      {o.cross_alias_verified_creation && (
                        <span className="text-[10px] font-mono-tech uppercase tracking-widest text-emerald-400 border border-emerald-500/40 bg-emerald-500/[0.06] px-2 py-0.5"
                              title={`Matched under: ${(o.matched_aliases || []).join(", ")}`}
                              data-testid="hunter-verified-creation">
                          ✓ Verified Creation
                        </span>
                      )}
                      {o.is_ugc && (
                        <span className="text-[10px] font-mono-tech uppercase tracking-widest text-cyan-300 border border-cyan-500/40 bg-cyan-500/[0.06] px-2 py-0.5" data-testid="hunter-ugc-badge">
                          UGC · $0.50/1k views
                        </span>
                      )}
                      {o.leak_multiplier > 1 && (
                        <span className="text-[10px] font-mono-tech uppercase tracking-widest text-amber-300 border border-amber-500/40 bg-amber-500/[0.06] px-2 py-0.5" title="+15% Global Admin — verify international collection to remove">
                          +15% Leak
                        </span>
                      )}
                      {o.streams > 0 && (
                        <span className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">
                          · {new Intl.NumberFormat("en-US").format(o.streams)} {o.is_ugc ? "views" : "streams"}
                        </span>
                      )}
                      {o.release_year && (
                        <span className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">· since {o.release_year}</span>
                      )}
                    </div>
                    <div className="font-medium text-white mb-1">{o.title}</div>
                    <div className="text-sm text-neutral-400 leading-relaxed">{o.recommended_action}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] font-mono-tech text-neutral-500">Your Share ({o.user_share_pct?.toFixed?.(0) || 25}%)</div>
                    <div className="font-display text-lg text-emerald-400">{fmtUsd(o.estimated_recovery_usd)}</div>
                    {o.total_revenue_usd && (
                      <div className="text-[10px] text-neutral-500 mt-0.5">of {fmtUsd(o.total_revenue_usd)}</div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        const type = o.fix_evidence_type || "royalty";
                        // Deep-link into the Sovereign Vault. Once the
                        // user PIN-unlocks and Evidence Vault mounts,
                        // it fires the `bc-fix-now` event with this
                        // evidence type so the correct form auto-opens.
                        try { sessionStorage.setItem("vault:fix-now", type); } catch { /* ignore */ }
                        window.location.href = `/vault?fixnow=${encodeURIComponent(type)}`;
                      }}
                      className="mt-2 inline-flex items-center gap-1 bg-amber-500 text-black hover:brightness-110 px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-[0.22em] font-semibold"
                      data-testid={`hunter-fix-now-${o.bounty_id}`}
                    >
                      Fix Now →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
        </div>)}
        </div>)}

        {activeFeature === "LH" && (<div data-testid="royai-feature-pane-LH">
        <Band title="Legacy Hunter · 20-year back-pay & ISRC recovery" accent="#F59E0B" />
        <div data-testid="royai-content-lh">
          <SectionCard>
            <div className="flex items-start gap-3 mb-3">
              <Clock size={20} className="text-amber-500 shrink-0 mt-1" />
              <div>
                <h3 className="font-display text-xl font-medium">Legacy Hunter</h3>
                <p className="text-xs text-neutral-500 mt-1 max-w-2xl">
                  Sweeps the last 20 years of registrations for missing ISRC
                  linkages, unclaimed SoundExchange neighboring rights, MLC
                  black-box distributions, and PRO back-catalog gaps.
                  Recoverable value is queued as bounties once matches are
                  confirmed against your verified evidence.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
              {[
                { label: "SoundExchange (2000–2020)", note: "Legacy neighboring-rights sweep" },
                { label: "MLC Black-Box",             note: "Unmatched mechanical distributions" },
                { label: "PRO Back-Catalog",          note: "3-year retroactive statement sweep" },
              ].map((row) => (
                <div key={row.label} className="bg-[#0c0c0c] p-5">
                  <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">{row.label}</div>
                  <div className="mt-2 font-mono-tech text-xs text-amber-400/80">Queued · awaiting evidence match</div>
                  <div className="mt-1 text-[11px] text-neutral-400 leading-relaxed">{row.note}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">
              Preview · full sweep engages once your Verified Evidence covers a track.
            </div>
          </SectionCard>
        </div>
        </div>)}

        {activeFeature === "SAL" && (<div data-testid="royai-feature-pane-SAL">

        {/* Smart Audit Link — universal link metadata importer. */}
        <Band title="Smart Audit Link" accent="#D4AF37" />
        <div data-testid="royai-content-sal">
        <div className="mb-6">
          <SmartAuditLink onAudited={async () => {
            const [avNew, rrNew, bnNew, hNew] = await Promise.all([
              royai.audit(), royai.revenueReport(), royai.bounties(), royai.hunter(),
            ]);
            setAu(avNew); setRr(rrNew); setBn(bnNew); setH(hNew);
          }} />
          <ManualEntryToggle>
            <TrackAuditForm existingTracks={au?.tracks || []} onAdded={async () => { setAu(await royai.audit()); }} />
          </ManualEntryToggle>
        </div>
        </div>
        </div>)}

        {activeFeature === "DDRR" && (<div data-testid="royai-feature-pane-DDRR">
        {/* Metadata Audit · Deep Dive Revenue Report */}
        <Band title="Metadata Audit · Deep Dive Revenue Report" accent="#10B981" />
        <div data-testid="royai-content-3">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-px bg-white/5 border border-white/5 mb-6">
          <SectionCard className="!border-0 flex flex-col items-center justify-center text-center py-8">
            <Shield size={32} className="text-emerald-400 mb-3" strokeWidth={1.4} />
            <div className="text-xs font-mono-tech uppercase tracking-[0.22em] text-neutral-400 mb-2">Audit Score</div>
            <div className="font-display text-5xl font-medium text-white mb-1" data-testid="audit-score">{au?.audit_score || 0}</div>
            <div className="text-xs font-mono-tech text-neutral-500">/ 100</div>
            <div className="mt-3 text-[10px] text-neutral-400">
              {au?.tracks_checked || 0} tracks · {au?.fields_checked || 0} fields
            </div>
            {au?.data_source === "user_entered" ? (
              <div className="mt-2 text-[10px] font-mono-tech uppercase tracking-widest text-emerald-400">Live</div>
            ) : (
              <div className="mt-2 text-[10px] font-mono-tech uppercase tracking-widest text-amber-500/70">Sample</div>
            )}
          </SectionCard>
          <SectionCard className="lg:col-span-3 !border-0">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div>
                <h3 className="font-display text-xl font-medium">Deep Dive Revenue Report</h3>
                <div className="text-xs text-neutral-500 mt-1">Per-platform revenue for every track you&apos;ve audited via Smart Audit Link.</div>
              </div>
              {rr?.totals?.your_share_usd > 0 && (
                <div className="text-right">
                  <div className="text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">Your Share (aggregate)</div>
                  <div className="font-display text-2xl text-emerald-400" data-testid="rr-header-share">{fmtUsdCents(rr.totals.your_share_usd)}</div>
                </div>
              )}
            </div>
            <DeepDiveRevenueReport report={rr} onDeleteTrack={async (t) => {
              try {
                await royai.deleteRevenueTrack(t.track_id);
                toast.success(`Removed "${t.title}"`);
                const [rrNew, bnNew, hNew] = await Promise.all([royai.revenueReport(), royai.bounties(), royai.hunter()]);
                setRr(rrNew); setBn(bnNew); setH(hNew);
              } catch (e) {
                console.error("delete audit failed", e);
                const msg = e?.response?.data?.detail || e?.message || "Delete failed";
                toast.error(typeof msg === "string" ? msg : "Delete failed");
              }
            }} onSplitChanged={async () => {
              try { setRr(await royai.revenueReport()); } catch { /* ignore */ }
            }} />
          </SectionCard>
        </div>
        </div>
        </div>)}

        {activeFeature === "RUOA" && (<div data-testid="royai-feature-pane-RUOA">
        {/* Royalty & UGC Opportunity Analyzer */}
        <Band title="Royalty & UGC Opportunity Analyzer" accent="#A78BFA" />
        <div className="mb-6" data-testid="royalty-analyzer-royai">
          <RoyaltyAnalyzer />
        </div>
        </div>)}

        {/* 04 — AI ACCOUNTING */}
        {activeFeature === "AIA" && (<div data-testid="royai-feature-pane-AIA">
        <Band number={4} title="AI Accounting" accent="#38BDF8" open={openSections[4]} onToggle={() => toggleSection(4)} />
        {openSections[4] && (<div data-testid="royai-content-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5 mb-6">
          <KpiCard testId="acct-income"   label="Income"          value={fmtUsd(ac?.income_usd)}       icon={DollarSign} accent="#10B981" />
          <KpiCard testId="acct-expenses" label="Expenses"        value={fmtUsd(ac?.expenses_usd)}     icon={Calculator} accent="#E11D48" />
          <KpiCard testId="acct-net"      label="Net Income"      value={fmtUsd(ac?.net_income_usd)}   icon={TrendingUp} />
          <KpiCard testId="acct-tax"      label="Est. Tax (25%)"  value={fmtUsd(ac?.tax_estimate_usd)} icon={AlertTriangle} accent="#F59E0B" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/5 border border-white/5 mb-6">
          <SectionCard className="lg:col-span-2 !border-0">
            <h3 className="font-display text-xl font-medium mb-6">Recent Expenses</h3>
            <div className="space-y-2 max-h-72 overflow-y-auto mb-6" data-testid="expenses-list">
              {(ac?.expenses || []).map((e, i) => (
                <div key={e.id || i} className="flex items-center justify-between gap-3 px-3 py-2 border border-white/5 bg-black/40 group">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{e.description}</div>
                    <div className="text-xs text-neutral-500 mt-0.5 flex items-center gap-2">
                      <span className="uppercase tracking-widest">{e.category}</span>
                      <span>·</span>
                      <span>{e.date}</span>
                      {e.placeholder && <span className="text-amber-500/70">(sample)</span>}
                    </div>
                  </div>
                  <div className="font-mono-tech text-sm text-rose-300 shrink-0">-{fmtUsdCents(e.amount_usd)}</div>
                  {e.id && !e.placeholder && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm(`Delete "${e.description}"?`)) return;
                        try {
                          await royai.deleteExpense(e.id);
                          setAc(await royai.accounting());
                          toast.success("Expense removed");
                        } catch { toast.error("Delete failed"); }
                      }}
                      className="opacity-60 group-hover:opacity-100 text-neutral-500 hover:text-rose-400 shrink-0 p-1"
                      aria-label="Delete expense"
                      data-testid={`expense-delete-${e.id}`}
                    >
                      <Minus size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <form onSubmit={submitExpense} className="grid grid-cols-1 sm:grid-cols-[130px_1fr_130px_auto] gap-2" data-testid="expense-form">
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="bg-black border border-white/10 px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="expense-category"
              >
                {["studio","equipment","software","travel","marketing","other"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input
                placeholder="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-black border border-white/10 px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="expense-description"
              />
              <input
                type="number" step="0.01" placeholder="Amount"
                value={form.amount_usd}
                onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
                className="bg-black border border-white/10 px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="expense-amount"
              />
              <button type="submit" className="bg-amber-500 text-black px-4 py-2 text-sm font-mono-tech uppercase tracking-widest hover:brightness-110 flex items-center gap-1" data-testid="expense-submit">
                <Plus size={14} /> Add
              </button>
            </form>
          </SectionCard>

          <SectionCard className="!border-0">
            <h3 className="font-display text-xl font-medium mb-6">By Category</h3>
            <div className="space-y-3" data-testid="expenses-by-category">
              {Object.entries(ac?.expenses_by_category || {}).map(([cat, amt]) => {
                const pct = Math.min(100, (amt / Math.max(ac?.expenses_usd || 1, 1)) * 100);
                return (
                  <div key={cat}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="uppercase tracking-widest text-neutral-400">{cat}</span>
                      <span className="text-neutral-300 font-mono-tech">{fmtUsdCents(amt)}</span>
                    </div>
                    <div className="h-1.5 bg-white/5 overflow-hidden">
                      <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        </div>

        {/* 05 — FORECAST ENGINE */}
        </div>)}
        </div>)}
        {activeFeature === "FE" && (<div data-testid="royai-feature-pane-FE">
        <Band number={5} title="Forecast Engine" accent="#A78BFA" open={openSections[5]} onToggle={() => toggleSection(5)} />
        {openSections[5] && (<div data-testid="royai-content-5">
        <TeaserOverlay featureKey="forecast_engine">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/5 border border-white/5 mb-6">
          <div className="lg:col-span-2 bg-[#0c0c0c] p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl font-medium">6-Month Forecast</h3>
              <div className="flex items-center gap-2">
                <LineIcon size={14} className="text-violet-400" />
                <span className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">{fc?.growth_pct}% growth · {fc?.confidence} confidence</span>
              </div>
            </div>
            <div className="h-64" data-testid="chart-forecast">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.3)" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="rgba(255,255,255,0.3)" fontSize={11} tickFormatter={(v) => `$${(v/1000).toFixed(1)}k`} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(167,139,250,0.4)", fontSize: 12 }} formatter={(v) => fmtUsdCents(v)} />
                  <Bar dataKey="predicted" fill="#A78BFA" radius={[2,2,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-[#0c0c0c] p-6 lg:p-8">
            <h3 className="font-display text-xl font-medium mb-6">Predictions</h3>
            <div className="space-y-4 mb-6">
              <div>
                <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500 mb-1">Predicted Monthly</div>
                <div className="font-display text-3xl text-white" data-testid="forecast-monthly">{fmtUsd(fc?.predicted_monthly_usd)}</div>
              </div>
              <div>
                <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500 mb-1">Predicted Annual</div>
                <div className="font-display text-3xl text-white" data-testid="forecast-annual">{fmtUsd(fc?.predicted_annual_usd)}</div>
              </div>
            </div>
            <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500 mb-2">Top Performing Songs</div>
            <div className="space-y-1.5" data-testid="top-songs">
              {(fc?.top_songs || []).slice(0, 5).map((s) => (
                <div key={s.title} className="flex items-center justify-between text-xs">
                  <span className="truncate text-neutral-300">{s.title}</span>
                  <span className={`shrink-0 ml-2 font-mono-tech ${s.growth_pct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {s.growth_pct >= 0 ? "+" : ""}{s.growth_pct}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        </TeaserOverlay>

        {/* 06 — AI ALERTS */}
        </div>)}
        </div>)}
        {false && (<div data-testid="royai-tabpane-recovery-06">
        <Band number={6} title="AI Alerts" accent="#F59E0B" open={openSections[6]} onToggle={() => toggleSection(6)} />
        {openSections[6] && (<div data-testid="royai-content-6">
        <TeaserOverlay featureKey="ai_alerts">
        <SectionCard>
          <div className="flex items-center gap-3 mb-6">
            <Bell size={20} className="text-amber-400" />
            <h3 className="font-display text-xl font-medium">Notification Center</h3>
            <span className="ml-auto text-xs font-mono-tech uppercase tracking-widest text-neutral-500">{(al?.alerts || []).length} active</span>
          </div>
          {(al?.alerts || []).length === 0 ? (
            <div className="text-sm text-neutral-500 py-4" data-testid="alerts-empty">No active alerts. You&apos;re all caught up.</div>
          ) : (
            <div className="space-y-3" data-testid="alerts-list">
              {(al?.alerts || []).map((a, i) => (
                <div key={i} className={`border px-4 py-4 ${SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.medium}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium mb-1">{a.title}</div>
                      <div className="text-sm text-neutral-300 leading-relaxed">{a.body}</div>
                    </div>
                    {a.estimated_usd && (
                      <div className="text-right shrink-0">
                        <div className="text-xs font-mono-tech text-neutral-500 mb-1">Recover</div>
                        <div className="font-display text-lg text-emerald-400">{fmtUsd(a.estimated_usd)}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
        </TeaserOverlay>
        </div>)}
        </div>)}

        {/* Verified Evidence tab — Evidence Vault + Chain of Title
            timeline. Given a dedicated tab so creators can focus on
            uploading proof and reviewing their forensic timeline without
            the noise of the other RoyAI workstreams. */}
        {activeFeature === "VE" && (<div data-testid="royai-feature-pane-VE">
          <div className="mb-6" data-testid="evidence-vault-royai">
            <EvidenceVault />
          </div>
        </div>)}

        {/* Catalog Summary tab — bottom-of-page totalizer + Finalize &
            Add to Portfolio, isolated so users can review their catalog
            state without scrolling past the other workstreams. */}
        {activeFeature === "CS" && (<div data-testid="royai-feature-pane-CS">
          <CatalogSummary />
        </div>)}
      </main>

      {/* Unlock Tool Modal — cinematic lock animation + daily VIP code
          input. Shown when a locked tile is tapped. On success the
          backend flips royai_unlocked=true and starts the 60-min
          countdown; we re-fetch access-status so the whole grid
          unlocks in one motion. */}
      {unlockingFeature && (
        <div
          data-testid="royai-unlock-modal"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-6 bg-black/85 backdrop-blur-sm animate-fade-in-up"
          onClick={(e) => { if (e.target === e.currentTarget) { setUnlockingFeature(null); setUnlockCode(""); } }}
        >
          <div className="relative w-full max-w-lg bg-[#0a0a0a] border border-amber-500/40 p-8 sm:p-10">
            {/* Close */}
            <button
              type="button"
              onClick={() => { setUnlockingFeature(null); setUnlockCode(""); }}
              data-testid="royai-unlock-modal-close"
              className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            {/* Cinematic lock animation */}
            <div className="flex flex-col items-center text-center">
              <div className="relative w-28 h-28 mb-6" aria-hidden>
                <div className="absolute inset-0 rounded-full border border-amber-500/30 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]" />
                <div
                  className="absolute inset-2 rounded-full border-2 border-amber-500/60"
                  style={{ animation: "spin 8s linear infinite" }}
                />
                <div
                  className="absolute inset-5 rounded-full border border-amber-500/40"
                  style={{ animation: "spin 5s linear infinite reverse" }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Lock
                    size={40}
                    className="text-amber-500"
                    strokeWidth={1.4}
                    style={{ filter: "drop-shadow(0 0 16px #D4AF37cc)" }}
                  />
                </div>
              </div>

              <div className="text-[10px] font-mono-tech uppercase tracking-[0.22em] text-amber-500 mb-2">
                {unlockingFeature.category}
              </div>
              <h3 className="font-display text-2xl sm:text-3xl leading-tight mb-2">
                Unlock {unlockingFeature.title}
              </h3>
              <p className="text-xs text-neutral-400 leading-relaxed max-w-sm mb-6">
                Redeem your <span className="text-amber-500">Daily Drop</span> VIP
                code to unlock the entire RoyAI Suite for the next 60 minutes.
                Founding-Producer members bypass this step permanently.
              </p>

              <form onSubmit={submitUnlock} className="w-full">
                <input
                  type="text"
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  value={unlockCode}
                  onChange={(e) => setUnlockCode(e.target.value)}
                  placeholder="Enter your VIP code"
                  data-testid="royai-unlock-code-input"
                  className="w-full text-center font-mono-tech text-lg uppercase tracking-[0.35em] bg-black border border-white/10 focus:border-amber-500 outline-none px-4 py-4 mb-4 text-white placeholder:text-neutral-600"
                />
                <button
                  type="submit"
                  disabled={unlockBusy}
                  data-testid="royai-unlock-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 text-black hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all px-6 py-4 font-mono-tech text-xs uppercase tracking-[0.22em] font-semibold"
                  style={{ boxShadow: "0 20px 60px -20px #D4AF37cc" }}
                >
                  {unlockBusy ? "Verifying…" : "Unlock Suite"}
                  {!unlockBusy && <ArrowRight size={14} />}
                </button>
              </form>

              <div className="mt-5 text-[10px] font-mono-tech uppercase tracking-[0.22em] text-neutral-500">
                Codes expire 60 minutes after redemption.
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
